import { useState, useCallback, useRef, useEffect } from 'react';
import { isSpeechRecognitionSupported } from '../utils/chatUtils';

interface UseVoiceRecognitionReturn {
  isRecording: boolean;
  startRecording: () => void;
  stopRecording: (shouldSendTranscript?: boolean) => void;
  isSupported: boolean;
  isRequestingPermission: boolean;
  currentTranscript: string;
  interimTranscript: string;
  resetTranscript: () => void;
}

export const useVoiceRecognition = (
  onResult: (transcript: string) => void,
  onError?: (error: string) => void
): UseVoiceRecognitionReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef<any>(null);
  const noSpeechTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speechEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSupported = isSpeechRecognitionSupported();

  // Cleanup function
  const cleanup = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (noSpeechTimeoutRef.current) {
      clearTimeout(noSpeechTimeoutRef.current);
      noSpeechTimeoutRef.current = null;
    }
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }
    setIsRecording(false);
    setIsRequestingPermission(false);
  }, []);

  // Reset transcript
  const resetTranscript = useCallback(() => {
    setCurrentTranscript('');
    setInterimTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      onError?.('Trình duyệt không hỗ trợ nhận dạng giọng nói');
      return;
    }

    // Don't start if already recording
    if (isRecording) {
      return;
    }

    // Cleanup existing recognition first
    cleanup();
    
    setIsRequestingPermission(true);
    resetTranscript();
    
    try {
      // Request microphone permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      setIsRequestingPermission(false);
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const newRecognition = new SpeechRecognition();

      newRecognition.continuous = true;
      newRecognition.interimResults = true;
      newRecognition.lang = 'vi-VN';
      newRecognition.maxAlternatives = 1;
      
      // Set timeout for speech end detection (3 seconds of silence)
      newRecognition.speechTimeout = 3000; // 3 seconds
      newRecognition.endSilenceTimeout = 2000; // 2 seconds

      newRecognition.onstart = () => {
        setIsRecording(true);
        
        // Set timeout for no speech detection (15 seconds - standard timeout)
        noSpeechTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        }, 15000);
      };

      newRecognition.onresult = (event: any) => {
        // Clear no speech timeout when we get results
        if (noSpeechTimeoutRef.current) {
          clearTimeout(noSpeechTimeoutRef.current);
          noSpeechTimeoutRef.current = null;
        }
        
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }

        // Cập nhật transcript hiện tại
        if (final) {
          setCurrentTranscript(prev => prev + final);
          setInterimTranscript('');
          
          // Set timeout for speech end detection (1.5 seconds after final result)
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
          }
          speechEndTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
          }, 1500); // 1.5 seconds of silence after final result
        } else {
          setInterimTranscript(interim);
          
          // Clear speech end timeout when we get interim results
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
            speechEndTimeoutRef.current = null;
          }
        }
      };

      newRecognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        
        // Don't show error for aborted (user manually stopped) or service-not-allowed
        if (event.error === 'aborted' || event.error === 'service-not-allowed') {
          cleanup();
          return;
        }
        
        cleanup();
        
        let errorMessage = 'Lỗi nhận dạng giọng nói';
        switch (event.error) {
          case 'no-speech':
            errorMessage = 'Không phát hiện giọng nói. Vui lòng thử lại.';
            break;
          case 'audio-capture':
            errorMessage = 'Không thể truy cập microphone';
            break;
          case 'not-allowed':
            errorMessage = 'Quyền truy cập microphone bị từ chối';
            break;
          case 'network':
            errorMessage = 'Lỗi mạng. Vui lòng kiểm tra kết nối.';
            break;
          case 'language-not-supported':
            errorMessage = 'Ngôn ngữ không được hỗ trợ';
            break;
          case 'bad-grammar':
            errorMessage = 'Lỗi ngữ pháp trong nhận dạng';
            break;
        }
        onError?.(errorMessage);
      };

      newRecognition.onend = () => {
        setIsRecording(false);
        
        // Clear all timeouts
        if (noSpeechTimeoutRef.current) {
          clearTimeout(noSpeechTimeoutRef.current);
          noSpeechTimeoutRef.current = null;
        }
        if (speechEndTimeoutRef.current) {
          clearTimeout(speechEndTimeoutRef.current);
          speechEndTimeoutRef.current = null;
        }
        
        // Khi tự động dừng, KHÔNG tự động gửi transcript
        // Chỉ dừng recording và giữ transcript để user có thể xem và quyết định
        // onResult sẽ được gọi khi user bấm "Gửi yêu cầu" hoặc "Stop" thủ công
        
        // Clear recognition reference to prevent memory leaks
        recognitionRef.current = null;
      };

      recognitionRef.current = newRecognition;
      newRecognition.start();
      
    } catch (error) {
      console.error('Microphone permission error:', error);
      cleanup();
      onError?.('Không thể truy cập microphone');
    }
  }, [isSupported, onError, cleanup, resetTranscript, isRecording]);

  const stopRecording = useCallback((shouldSendTranscript: boolean = true) => {
    if (recognitionRef.current && isRecording) {
      
      // Set recording to false immediately for UI responsiveness
      setIsRecording(false);
      
      // Get transcript before stopping
      const finalText = (currentTranscript + interimTranscript).trim();
      
      // Stop recognition
      recognitionRef.current.stop();
      
      // Chỉ gửi transcript khi shouldSendTranscript = true
      if (shouldSendTranscript && finalText) {
        onResult(finalText);
        // Reset transcript sau khi gửi
        setTimeout(() => {
          resetTranscript();
        }, 50);
      }
      // Nếu không gửi transcript, giữ nguyên transcript để user có thể xem
    } else {
      // If not recording, just set to false
      setIsRecording(false);
    }
  }, [isRecording, currentTranscript, interimTranscript, onResult, resetTranscript]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    isSupported,
    isRequestingPermission,
    currentTranscript,
    interimTranscript,
    resetTranscript
  };
};